import { useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { createPurchaseOrder } from 'purchase-motions-api/mutation/purchaseOrderGeneration';
import { useGlobalAlertStore } from '../../../hooks/useGlobalAlertStore';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { useIsSeats } from '../../../hooks/useIsSeats';
import { incrementPurchaseOrderGenerationFailure, incrementPurchaseOrderGenerationSuccess } from '../../../utils/metrics';
import { getIsInterstitialPage, getPurchaseRequestAsQueryParams, getQueryParams } from '../../../utils/queryParams';
import { getCheckoutUrl, getInterstitialUrl, navigateTo } from '../../../utils/urls';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { RequestProductButton } from '../RequestProductButton/RequestProductButton';
import { RequestSeatButton } from '../RequestSeatButton/RequestSeatButton';
import { TalkToSalesButton } from '../TalkToSalesButton/TalkToSalesButton';
import { logPurchaseOrderError } from './purchase-order-error-logger';

/**
 * This component is used to render a button that allows the user to buy a product.
 * It is used for all buying purchase motions.
 * It internally handles the logic for interstitials, admin modal, and purchase order generation.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BuyingButtonBase = props => {
  const {
    'data-test-id': dataTestId,
    'data-selenium-id': dataSeleniumId,
    apiName,
    children,
    location,
    interstitialQueryParams,
    purchaseMotion,
    redirectQueryParams,
    shouldDisplayAlertOnFailure = true,
    skipInterstitial,
    trackingData,
    upgradeConfig,
    uiButtonProps,
    onClick,
    onPurchaseOrderGenerationSuccess,
    onPurchaseOrderGenerationFailure,
    onInterstitialRedirect,
    isInterstitialPage,
    isUpgradePoint
  } = props;
  const purchaseRequest = useMemo(() => {
    if ('purchaseRequest' in props && props.purchaseRequest) {
      return props.purchaseRequest;
    }
    if ('currencyCode' in props && props.currencyCode) {
      var _props$terms, _props$terms2;
      return {
        products: [{
          productApiName: apiName,
          incrementalQuantity: 1
        }],
        quantityPacks: [],
        currencyCode: props.currencyCode,
        commitmentTerm: (_props$terms = props.terms) === null || _props$terms === void 0 ? void 0 : _props$terms.commitmentTerm,
        paymentTerm: (_props$terms2 = props.terms) === null || _props$terms2 === void 0 ? void 0 : _props$terms2.paymentTerm
      };
    }
    throw new Error('BuyingButton requires either purchaseRequest or currencyCode');
  }, [apiName, props]);
  const apiNames = useMemo(() => {
    var _purchaseRequest$prod, _purchaseRequest$quan;
    return Array.from(new Set([...((purchaseRequest === null || purchaseRequest === void 0 || (_purchaseRequest$prod = purchaseRequest.products) === null || _purchaseRequest$prod === void 0 ? void 0 : _purchaseRequest$prod.map(product => product.productApiName)) || []), ...((purchaseRequest === null || purchaseRequest === void 0 || (_purchaseRequest$quan = purchaseRequest.quantityPacks) === null || _purchaseRequest$quan === void 0 ? void 0 : _purchaseRequest$quan.map(pack => `${String(pack.quantityName)}_${pack.parentProductApiName}`)) || [])]));
  }, [purchaseRequest]);
  const trackingProperties = useMemo(() => {
    const properties = getTrackingProperties({
      apiName,
      trackingData,
      upgradeConfig,
      sourceKey: purchaseMotion
    });
    return Object.assign({}, properties, {
      apiNames,
      checkoutType: purchaseMotion
    });
  }, [apiName, apiNames, purchaseMotion, trackingData, upgradeConfig]);
  const [purchaseOrderGenerating, setPurchaseOrderGenerating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const {
    alertStore,
    shouldRenderGlobalList
  } = useGlobalAlertStore();
  const isSeats = useIsSeats();
  const isAdmin = useIsAdmin('BILLING');

  // wait for the admin and seats checks to complete before showing the button
  const buttonReady = isAdmin !== null && isSeats !== null;

  // check which purchase motions are eligible for an interstitial
  // we need this check because seats and pre-seats addtl. contacts use a shared interstitial
  const isPurchaseMotionEligibleForInterstitial = purchaseMotion === 'BUY_ADDITIONAL_CONTACTS' || purchaseMotion === 'BUY_ADDITIONAL_SEATS' || isSeats && purchaseMotion === 'BUY_TOUCHLESS';
  const isOnInterstitialPage = isInterstitialPage !== null && isInterstitialPage !== void 0 ? isInterstitialPage : getIsInterstitialPage(location);
  const shouldGoToInterstitial = isPurchaseMotionEligibleForInterstitial && !isOnInterstitialPage && !skipInterstitial;
  const buttonRef = useRef(null);
  useIntersectionObserver({
    targetRef: buttonReady ? buttonRef : undefined,
    onIntersect: () => {
      track('checkout-button', Object.assign({
        action: 'viewed'
      }, trackingProperties));
    }
  });
  const handleClick = async () => {
    onClick === null || onClick === void 0 || onClick(purchaseMotion);
    track('checkout-button', Object.assign({
      action: 'clicked'
    }, trackingProperties));
    if (isUpgradePoint) {
      track('interaction', Object.assign({
        action: 'clicked upgrade point'
      }, trackingProperties));
    }
    if (purchaseMotion === 'BUY_ADDITIONAL_SEATS') {
      track('add-additional-seats-click', {
        apiName,
        trackingSource: trackingProperties.source
      });
    }

    // go to interstitial
    if (shouldGoToInterstitial) {
      var _String;
      track('interaction', Object.assign({
        action: 'clicked interstitial redirect'
      }, trackingProperties));
      const queryParams = getQueryParams();
      const productsAsQueryParams = getPurchaseRequestAsQueryParams(purchaseRequest);
      const interstitialUrl = getInterstitialUrl(Object.assign({
        upgradeSource: (trackingProperties === null || trackingProperties === void 0 ? void 0 : trackingProperties.upgradeSource) || '',
        purchaseMotion
      }, queryParams, productsAsQueryParams, purchaseRequest.currencyCode ? {
        currencyCode: String(purchaseRequest.currencyCode)
      } : {}, purchaseRequest.paymentTerm ? {
        term: (_String = String(purchaseRequest.paymentTerm)) === null || _String === void 0 ? void 0 : _String.toLowerCase()
      } : {}, interstitialQueryParams ? Object.assign({}, interstitialQueryParams) : {}));
      onInterstitialRedirect === null || onInterstitialRedirect === void 0 || onInterstitialRedirect();
      navigateTo(interstitialUrl);
      return;
    }

    // generate purchase order
    try {
      var _purchaseRequest$quan2, _purchaseRequest$quan3;
      setHasError(false);
      setPurchaseOrderGenerating(true);
      const {
        purchaseOrderId
      } = await createPurchaseOrder(Object.assign({}, purchaseRequest, {
        options: {
          useQuantityPacksOnly: ((_purchaseRequest$quan2 = purchaseRequest === null || purchaseRequest === void 0 || (_purchaseRequest$quan3 = purchaseRequest.quantityPacks) === null || _purchaseRequest$quan3 === void 0 ? void 0 : _purchaseRequest$quan3.length) !== null && _purchaseRequest$quan2 !== void 0 ? _purchaseRequest$quan2 : 0) > 0,
          onboardingSkipped: false
        }
      }));
      track('checkout-button', Object.assign({
        action: 'purchase order generated'
      }, trackingProperties));
      incrementPurchaseOrderGenerationSuccess(apiNames);
      const checkoutUrl = getCheckoutUrl(purchaseOrderId, {
        upgradeSource: trackingProperties === null || trackingProperties === void 0 ? void 0 : trackingProperties.upgradeSource
      });
      const redirectUrl = new URL(checkoutUrl);

      // add any redirect params to the checkout URL if provided
      if (redirectQueryParams) {
        Object.entries(redirectQueryParams).forEach(([key, value]) => {
          redirectUrl.searchParams.append(key, value.toString());
        });
      }
      onPurchaseOrderGenerationSuccess === null || onPurchaseOrderGenerationSuccess === void 0 || onPurchaseOrderGenerationSuccess(purchaseOrderId);
      navigateTo(redirectUrl.toString());
    } catch (error) {
      track('checkout-button', Object.assign({
        action: 'purchase order generation failed'
      }, trackingProperties));
      incrementPurchaseOrderGenerationFailure(apiNames);
      if (shouldDisplayAlertOnFailure) {
        setHasError(true);
        alertStore.addAlert({
          id: 'purchase-order-generation-failure',
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.buyNowFailureAlert.title"
          }),
          message: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "purchaseMotions.buyNowFailureAlert.message"
            }), /*#__PURE__*/_jsx(ButtonWrapper, {
              children: /*#__PURE__*/_jsx(Button, {
                use: "tertiary",
                size: "extra-small",
                onClick: () => {
                  var _document$getElementB;
                  alertStore.removeAlert('purchase-order-generation-failure');
                  (_document$getElementB = document.getElementById('hidden-talk-to-sales-button')) === null || _document$getElementB === void 0 || _document$getElementB.click();
                },
                children: "Talk to Sales"
              })
            })]
          }),
          type: 'danger'
        });
      }
      const errorToLog = error instanceof Error ? error : new Error('Unknown error', {
        cause: error
      });
      logPurchaseOrderError(errorToLog, Object.assign({}, trackingProperties, {
        buttonName: `${purchaseMotion}_BUTTON`,
        request: purchaseRequest
      }));
      onPurchaseOrderGenerationFailure === null || onPurchaseOrderGenerationFailure === void 0 || onPurchaseOrderGenerationFailure(new Error('Purchase order generation failed', {
        cause: error
      }));
    } finally {
      setPurchaseOrderGenerating(false);
    }
  };
  const buttonText = useMemo(() => {
    if (children) {
      return children;
    }
    switch (purchaseMotion) {
      case 'BUY_ADDITIONAL_SEATS':
        {
          return isSeats ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.buttons.buyAdditionalSeats"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "purchaseMotions.buttons.buyAdditionalUsers"
          });
        }
      case 'BUY_ADDITIONAL_CONTACTS':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.buttons.buyAdditionalContacts"
        });
      case 'BUY_TOUCHLESS':
      default:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "purchaseMotions.buttons.buyTouchless"
        });
    }
  }, [children, purchaseMotion, isSeats]);
  const renderRequestButton = () => {
    if (purchaseMotion === 'BUY_ADDITIONAL_SEATS') {
      return /*#__PURE__*/_jsx(RequestSeatButton, {
        "data-test-id": dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'buy-now-button',
        "data-selenium-id": dataSeleniumId,
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        onClick: onClick,
        uiButtonProps: uiButtonProps,
        showSeatSelector: isSeats !== null && isSeats !== void 0 ? isSeats : false,
        isUpgradePoint: isUpgradePoint,
        children: buttonText
      });
    }
    return /*#__PURE__*/_jsx(RequestProductButton, {
      "data-test-id": dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'buy-now-button',
      "data-selenium-id": dataSeleniumId,
      apiName: apiName,
      location: location,
      trackingData: trackingData,
      upgradeConfig: upgradeConfig,
      onClick: onClick,
      uiButtonProps: uiButtonProps,
      isUpgradePoint: isUpgradePoint,
      children: buttonText
    });
  };
  if (!buttonReady || purchaseOrderGenerating) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, uiButtonProps, {
      loading: true,
      children: buttonText
    }));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [shouldRenderGlobalList && (!isAdmin || shouldDisplayAlertOnFailure) && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "global-floating-alert-list",
      alertStore: alertStore
    }), !isAdmin && renderRequestButton(), isAdmin && /*#__PURE__*/_jsxs(_Fragment, {
      children: [shouldDisplayAlertOnFailure && hasError && /*#__PURE__*/_jsx(View, {
        display: "none",
        children: /*#__PURE__*/_jsx(TalkToSalesButton, {
          apiName: apiName,
          location: location,
          trackingData: Object.assign({}, trackingData, {
            uniqueId: 'checkout-error'
          }),
          upgradeConfig: upgradeConfig,
          uiButtonProps: {
            id: 'hidden-talk-to-sales-button'
          }
        })
      }), /*#__PURE__*/_jsx(Button, Object.assign({
        buttonRef: buttonRef,
        "data-test-id": dataTestId !== null && dataTestId !== void 0 ? dataTestId : 'buy-now-button',
        "data-selenium-id": dataSeleniumId,
        truncate: true,
        truncateStringProps: {
          justifyContent: 'center'
        }
      }, uiButtonProps, {
        onClick: () => {
          void handleClick();
        },
        children: buttonText
      }))]
    })]
  });
};
export const BuyingButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.BUYING_BUTTON,
  children: /*#__PURE__*/_jsx(BuyingButtonBase, Object.assign({}, props))
});